import React from 'react';
import {Link, useParams} from 'react-router-dom';
import {ArrowLeftIcon, ShirtIcon} from '../components/icons';
import {ThemeToggle} from '../components/ThemeToggle';
import {appName} from '../components/landing/content';
import {findLegalDocument, LegalBlock, LegalDocument, LegalSection} from '../legal';
import NotFound from './NotFound';
import {useDocumentTitle} from '../hooks/useDocumentTitle';

/**
 * Публичная страница правового документа (`/legal/:slug`).
 *
 * Открыта без аутентификации: соглашение нужно прочитать до регистрации, то есть
 * до первого JWT (requirements.md, P-1). Страница полностью статична — ни одного
 * обращения к API (P-2).
 *
 * Вёрстка подчинена одному: длинный текст должны дочитать. Колонка держится в
 * пределах ~70 символов, оглавление даёт доступ к разделу без прокрутки всего
 * документа, заголовки различимы по размеру, а не только по жирности.
 */

// `new Date('YYYY-MM-DD')` трактуется как UTC-полночь и в западных поясах
// показывает предыдущий день; собираем локальную дату из частей.
const formatDate = (iso: string): string => {
    const [year, month, day] = iso.split('-').map(Number);
    return new Date(year, month - 1, day)
        .toLocaleDateString('ru-RU', {day: 'numeric', month: 'long', year: 'numeric'});
};

const Block: React.FC<{block: LegalBlock}> = ({block}) => {
    if (block.kind === 'p') {
        return <p className="mt-4 text-base leading-7 text-ink-700 dark:text-ink-200">{block.text}</p>;
    }

    if (block.kind === 'list') {
        return (
            <ul className="mt-4 space-y-2">
                {block.items.map(item => (
                    <li
                        key={item}
                        className="relative pl-5 text-base leading-7 text-ink-700 dark:text-ink-200
                                   before:absolute before:left-0 before:top-[0.7em] before:h-1.5 before:w-1.5
                                   before:rounded-full before:bg-brand-500"
                    >
                        {item}
                    </li>
                ))}
            </ul>
        );
    }

    return (
        // Таблицы в правовом тексте узкие по смыслу, но на 320px всё равно не влезают:
        // скроллим саму таблицу, а не страницу.
        <div className="mt-5 -mx-4 sm:mx-0 overflow-x-auto">
            <table className="min-w-full text-sm border-collapse px-4">
                <thead>
                    <tr className="border-b border-ink-200 dark:border-white/10">
                        {block.head.map((cell, index) => (
                            <th
                                key={index}
                                className="text-left font-semibold text-ink-900 dark:text-ink-50 py-2.5 pr-6 align-top"
                            >
                                {cell}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {block.rows.map((row, rowIndex) => (
                        <tr key={rowIndex} className="border-b border-ink-100 dark:border-white/5 align-top">
                            {row.map((cell, cellIndex) => (
                                <td key={cellIndex} className="py-3 pr-6 leading-6 text-ink-700 dark:text-ink-200">
                                    {cell}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

const Section: React.FC<{section: LegalSection}> = ({section}) => (
    <section id={section.id} className="scroll-mt-24 pt-10" aria-labelledby={`${section.id}-title`}>
        <h2
            id={`${section.id}-title`}
            className="text-xl sm:text-2xl font-bold tracking-[-0.02em] text-ink-900 dark:text-ink-50"
        >
            {section.title}
        </h2>
        {section.blocks.map((block, index) => (
            <Block key={`${section.id}-${index}`} block={block}/>
        ))}
    </section>
);

const DocumentView: React.FC<{document: LegalDocument}> = ({document: doc}) => (
    <div className="min-h-screen bg-ink-50 dark:bg-ink-950" data-testid="legal-document">
        <header className="print:hidden sticky top-0 z-40 backdrop-blur bg-white/85 dark:bg-ink-950/85 border-b border-ink-100 dark:border-white/5">
            <div className="mx-auto w-full max-w-[900px] px-4 md:px-6 h-16 flex items-center gap-3">
                <Link to="/" className="flex items-center gap-2 min-w-0" aria-label="На главную">
                    <span className="w-8 h-8 rounded-xl bg-brand-500 flex items-center justify-center shrink-0">
                        <ShirtIcon className="w-4 h-4 text-ink-900"/>
                    </span>
                    <span className="font-semibold text-ink-900 dark:text-ink-50 truncate">{appName()}</span>
                </Link>
                <div className="ml-auto flex items-center gap-2">
                    <ThemeToggle/>
                    <Link to="/" className="btn-secondary h-10">
                        <ArrowLeftIcon className="w-4 h-4"/>
                        <span className="hidden sm:inline">На главную</span>
                    </Link>
                </div>
            </div>
        </header>

        <main className="mx-auto w-full max-w-[900px] px-4 md:px-6 pb-24">
            <div className="pt-12 md:pt-16">
                <h1 className="text-3xl sm:text-4xl font-bold leading-[1.1] tracking-[-0.03em] text-ink-900 dark:text-ink-50 text-balance">
                    {doc.title}
                </h1>
                <p className="mt-4 text-sm text-ink-500 dark:text-ink-400">
                    Версия {doc.version} · действует с {formatDate(doc.effectiveOn)}
                </p>
                <p className="mt-6 text-base leading-7 text-ink-600 dark:text-ink-300 max-w-[68ch]">{doc.intro}</p>
            </div>

            <nav className="print:hidden mt-10 rounded-2xl border border-ink-100 dark:border-white/5 bg-white dark:bg-ink-900 p-5"
                 aria-label="Содержание документа">
                <h2 className="text-sm font-semibold text-ink-900 dark:text-ink-50">Содержание</h2>
                <ol className="mt-3 grid sm:grid-cols-2 gap-x-6 gap-y-1.5">
                    {doc.sections.map(section => (
                        <li key={section.id}>
                            <a
                                href={`#${section.id}`}
                                className="text-sm leading-6 text-brand-600 dark:text-brand-400 hover:underline"
                            >
                                {section.title}
                            </a>
                        </li>
                    ))}
                </ol>
            </nav>

            <div className="max-w-[68ch]">
                {doc.sections.map(section => (
                    <Section key={section.id} section={section}/>
                ))}
            </div>
        </main>
    </div>
);

const LegalDocumentPage: React.FC = () => {
    const {slug} = useParams<{slug: string}>();
    const doc = findLegalDocument(slug);

    useDocumentTitle(doc?.title);

    // Неизвестный slug — обычная 404 приложения, а не пустая страница (EDGE-6).
    if (!doc) return <NotFound/>;

    return <DocumentView document={doc}/>;
};

export default LegalDocumentPage;
